import axios from '@/utils/axios'
import i18n from '@/i18n/i18n'
import { normalizeError } from '@/utils/normalize-error.js'

/** @typedef {import('@/types/models').AgentLoginPermission} AgentLoginPermission */
/** @typedef {import('@/types/models').UnsavedAgentLoginPermission} UnsavedAgentLoginPermission */
/** @typedef {import('@/types/models').User} User */

export const Role = {
  ADMIN: {
    id: 'admin',
    name: i18n.t('user.role.admin'),
  },
  ANALYTICS: {
    id: 'analytics',
    name: i18n.t('user.role.analytics'),
  },
  CUSTOMER_SUPPORT: {
    id: 'customer_support',
    name: i18n.t('user.role.customer_support'),
  },
  FINANCE: {
    id: 'finance',
    name: i18n.t('user.role.finance'),
  },
  MARKETING: {
    id: 'marketing',
    name: i18n.t('user.role.marketing'),
  },
  PACKER: {
    id: 'packer',
    name: i18n.t('user.role.packer'),
  },
  PICKUPS: {
    id: 'pickups',
    name: i18n.t('user.role.pickups'),
  },
  RETURNS: {
    id: 'returns',
    name: i18n.t('user.role.returns'),
  },
  SHIPPING: {
    id: 'shipping',
    name: i18n.t('user.role.shipping'),
  },
  WAREHOUSE: {
    id: 'warehouse',
    name: i18n.t('user.role.warehouse'),
  },
}

const ROLES = [
  Role.ADMIN,
  Role.ANALYTICS,
  Role.CUSTOMER_SUPPORT,
  Role.FINANCE,
  Role.MARKETING,
  Role.PACKER,
  Role.PICKUPS,
  Role.RETURNS,
  Role.SHIPPING,
  Role.WAREHOUSE,
]

export default {
  /**
   * @returns {Promise<User>}
   */
  async getMe() {
    const { data } = await axios.get('/xhr/users/me')
    return data
  },

  /**
   * @returns {Promise<User>}
   */
  async update(payload) {
    const { data } = await axios.patch('/xhr/users/me', payload)
    return data
  },

  /**
   * @returns {Promise<undefined>}
   */
  async delete() {
    const { data } = await axios.delete('/xhr/users/me')
    return data
  },

  async unfreeze() {
    const { data } = await axios.post('/xhr/users/me/unfreeze')
    return data
  },

  async resendVerificationEmail() {
    const { data } = await axios.post('/xhr/users/me/resend_verification_email', {})
    return data
  },

  async acceptedTermsAndConditions() {
    const { data } = await axios.post('/xhr/users/me/accept_terms_conditions', {})
    return data
  },

  async getShippingPrices(choice, fromCountry, toCountry) {
    const { data } = await axios.get('/xhr/users/me/shipping_prices', {
      params: {
        choice,
        from_country: fromCountry,
        to_country: toCountry,
      },
    })
    return data
  },

  async getShippingPrices2(params) {
    const { data } = await axios.get(`/xhr/users/me/shipping_prices?${params}`)
    return data
  },

  async exportUserData() {
    const { data } = await axios.post('/xhr/users/export_user_data')
    return data
  },

  async getUserData() {
    const { data } = await axios.get('/xhr/users/exported_user_data')
    return data
  },

  async updateVolumeSelection(payload) {
    const { data } = await axios.post('/xhr/users/me/update_volume_selection', payload)
    return data
  },

  async stopAgentLoginSession() {
    const response = await axios({
      method: 'post',
      url: '/hijack/release/',
    })
    return response.data
  },

  /**
   * Retrieves the granted agent login permission for the currently active user.
   *
   * @returns {Promise<AgentLoginPermission>}
   */
  async getAgentLoginPermission() {
    try {
      const response = await axios.get('/xhr/users/agent-login-permission')
      return response.data
    } catch (error) {
      throw normalizeError(error, i18n.t('Could not load agent login permission data.'))
    }
  },

  /**
   * Grants an agent login permission for a limited amount of time.
   *
   * @param {UnsavedAgentLoginPermission} agentLoginPermission
   * @returns {Promise<AgentLoginPermission>}
   */
  async grantAgentLoginPermission(agentLoginPermission) {
    try {
      const response = await axios.post('/xhr/users/agent-login-permission', agentLoginPermission)
      return response.data
    } catch (error) {
      throw normalizeError(error, i18n.t('Could not grant agent login permission data.'))
    }
  },

  /**
   * Revokes an agent login permission.
   *
   * @returns {Promise<AgentLoginPermission>}
   */
  async revokeAgentLoginPermission() {
    try {
      const response = await axios.delete('/xhr/users/agent-login-permission')
      return response.data
    } catch (error) {
      throw normalizeError(error, i18n.t('Could not revoke agent login permission data.'))
    }
  },

  async getUserManageableBetaFeatures() {
    try {
      const response = await axios.get('/xhr/users/beta-features')
      return response.data
    } catch (error) {
      throw normalizeError(error, i18n.t('Could not load user beta access list.'))
    }
  },

  async updateUserManageableBetaFeatures(betaFeature) {
    const betaFeatureAction = betaFeature.isEnabled ? 'subscribe' : 'unsubscribe'
    try {
      const response = await axios.post(`/xhr/users/beta-features/${betaFeatureAction}`, [betaFeature.flag])
      return response.data
    } catch (error) {
      throw normalizeError(error, i18n.t('Could not update user beta access list.'))
    }
  },

  /**
   * Returns all the roles that are available for this user. Not to be mistaken
   * with roles that are _assigned_ to the user.
   */
  getAvailableRoles(_user) {
    return ROLES
  },

  getRoleById(id) {
    return ROLES.find(r => r.id === id)
  },

  /**
   * Returns the roles as an alphabetically sorted, comma-separated string
   * @param { Array<Role> } roles
   * @returns {string}
   */
  getRolesLabel(roles) {
    return roles
      .map(id => this.getRoleById(id).name)
      .sort()
      .join(', ')
  },
}
